import {
  UseQueryResult,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useAuth, authorizedAxios, toast } from "@moonscape/shared";
import { minutes } from "@/utils";

export interface LossWriteoffClaim {
  id: number;
  userid: number;
  asset: string;
  coin_id: number | null;
  exchange: string | null;
  marked_at: string;
  notes: string | null;
}

export interface LossWriteoffClaimsResponse {
  success: boolean;
  data: LossWriteoffClaim[];
}

export const useLossWriteoffClaimsQueryKey = ["lossWriteoffClaims"];

export const useLossWriteoffClaims = () => {
  const { user, isAuthenticated, isLoading, getToken } = useAuth();
  const email = user?.email;
  const queryClient = useQueryClient();

  const getClaims = async (): Promise<LossWriteoffClaim[]> => {
    const url = `/loss-writeoff-claims`;
    const token = await getToken();
    const res = await authorizedAxios(token).get<LossWriteoffClaimsResponse>(url);
    return res.data.data;
  };

  const {
    isLoading: isQueryLoading,
    isIdle,
    isSuccess,
    error,
    isError,
    data: claims,
    refetch,
  }: UseQueryResult<LossWriteoffClaim[], unknown> = useQuery({
    queryKey: useLossWriteoffClaimsQueryKey,
    queryFn: getClaims,
    enabled: !!email,
    refetchOnWindowFocus: true,
    staleTime: 1 * minutes,
    refetchInterval: 5 * minutes,
  });

  const createClaim = async (data: { asset: string; reason?: string }) => {
    const token = await getToken();
    const url = `/loss-writeoff-claims`;
    const response = await authorizedAxios(token).post(url, data);
    return response.data;
  };

  const deleteClaim = async (claimId: number) => {
    const token = await getToken();
    const url = `/loss-writeoff-claims/${claimId}`;
    const response = await authorizedAxios(token).delete(url);
    return response.data;
  };

  const createMutation = useMutation({
    mutationFn: createClaim,
    onMutate: async () => {
      toast.success("Creating loss writeoff claim...");
    },
    onSuccess: () => {
      queryClient.invalidateQueries(useLossWriteoffClaimsQueryKey);
      queryClient.invalidateQueries(["assetCostBasis"]); // Invalidate cost basis too
      toast.success("Loss writeoff claim created successfully");
    },
    onError: (error: any) => {
      const errorMsg = error.response?.data?.error || "Error creating claim";
      toast.error(errorMsg);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteClaim,
    onMutate: async () => {
      toast.success("Deleting loss writeoff claim...");
    },
    onSuccess: () => {
      queryClient.invalidateQueries(useLossWriteoffClaimsQueryKey);
      queryClient.invalidateQueries(["assetCostBasis"]); // Invalidate cost basis too
      toast.success("Loss writeoff claim deleted successfully");
    },
    onError: (error: any) => {
      const errorMsg = error.response?.data?.error || "Error deleting claim";
      toast.error(errorMsg);
    },
  });

  return {
    isQueryLoading,
    isIdle,
    error,
    claims,
    email,
    refetch,
    createMutation,
    deleteMutation,
  };
};
